import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Briefcase, ChevronRight, Phone, TrendingUp, Award, Users, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';
import api from '@/utils/api';

const Builders = () => {
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBuilders = async () => {
      try {
        const response = await api.get('/builders');
        setBuilders(response.data);
      } catch (error) {
        console.error('Error fetching builders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBuilders();
  }, []);

  return (
    <div className="min-h-screen bg-dark" data-testid="builders-page">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden mt-32 bg-gradient-to-br from-dark via-dark-light to-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-gold/20 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-rose-gold text-sm mb-6 uppercase tracking-widest font-semibold\">Official Channel Partner</p>
            <h1 className="font-heading text-6xl md:text-8xl font-bold text-white mb-6 leading-tight">
              Premium <span className="text-rose-gold">Builders</span>
            </h1>
            <p className="text-xl text-white/70 mb-4 max-w-3xl mx-auto">
              Partnered with India's most trusted property developers
            </p>
            <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed mb-8">
              As an official channel partner, we offer exclusive access to projects from leading builders with the best prices and personalized guidance.
            </p>
            <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
              <Phone size={18} />
              Call for Builder Recommendations
            </a>
          </motion.div>
        </div>
      </section>

      {/* Why Channel Partner */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-dark">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Benefits of Booking Through <span className="text-rose-gold">PropVanta</span>
            </h2>
            <p className="text-white/70 text-lg">
              Direct partnerships with builders mean better deals and exclusive access for you
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
          >
            <TrendingUp className="text-rose-gold mb-6 mx-auto" size={40} />
            <h3 className="font-heading text-2xl font-semibold text-white mb-3">Best Prices</h3>
            <p className="text-white/60">Exclusive channel partner pricing and flexible payment plans</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
          >
            <Award className="text-rose-gold mb-6 mx-auto" size={40} />
            <h3 className="font-heading text-2xl font-semibold text-white mb-3">Pre-Launch Access</h3>
            <p className="text-white/60">First access to new projects before public launch</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
          >
            <Users className="text-rose-gold mb-6 mx-auto" size={40} />
            <h3 className="font-heading text-2xl font-semibold text-white mb-3">Personal Support</h3>
            <p className="text-white/60">Dedicated assistance from inquiry to possession</p>
          </motion.div>
        </div>
      </section>

      {/* Builders Grid */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark-light/50">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Premium Partners</p>
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Explore Projects by Builder
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Select a builder to view their exclusive projects and book personalized site visits
            </p>
          </motion.div>
        </div>

        {loading ? (
          <div className="text-center text-white/60">Loading builders...</div>
        ) : builders.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="builders-grid">
            {builders.map((builder, index) => (
              <motion.a
                key={builder.name}
                href={`/builders/${encodeURIComponent(builder.name)}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                data-testid={`builder-card-${builder.name}`}
                className="group glass-effect border-2 border-dark-lighter hover:border-rose-gold/50 rounded-2xl p-10 transition-all duration-500 shadow-lg hover:shadow-rose-glow"
              >
                <div className="flex items-center justify-center w-24 h-24 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-8 shadow-rose-glow group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="text-white" size={40} />
                </div>
                <h3 className="font-heading text-3xl font-bold text-white mb-4 group-hover:text-rose-gold transition-colors">
                  {builder.name}
                </h3>
                <div className="flex items-center gap-2 text-white/60 mb-6">
                  <Briefcase size={18} />
                  <span className="font-semibold">{builder.project_count} Projects</span>
                </div>
                <div className="mt-auto">
                  <span className="text-rose-gold font-semibold group-hover:underline inline-flex items-center gap-2">
                    View Projects 
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-white/60 text-lg">No builders available at the moment.</p>
          </div>
        )}
      </section>

      {/* Lead Generation Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-dark to-dark-light">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Get Expert Advice</p>
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Not Sure Which Builder
              <br />
              <span className="text-rose-gold">To Choose?</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              We'll help you compare projects from different builders based on your budget, location preference, and requirements. Get personalized recommendations with the best prices.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <TrendingUp className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Compare prices across multiple builders</p>
              </div>
              <div className="flex items-start gap-3">
                <Award className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Get honest recommendations based on your needs</p>
              </div>
              <div className="flex items-start gap-3">
                <Users className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Free site visits to shortlisted projects</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
                <Phone size={18} />
                Call Now
              </a>
              <a href="https://wa.me/917020970161" target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-2 text-sm uppercase tracking-wider border-green-500 text-green-500 hover:bg-green-500 hover:text-white">
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LeadForm 
              leadType="builder_inquiry" 
              title="Get Builder Recommendations"
            />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Builders;